"use client";

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import Link from "next/link";
import Image from "next/image";
import { IconArrowUp, IconChevronDown, IconMail, IconPhone, IconPlayerPlayFilled } from "@tabler/icons-react";
import { CopyrightYear } from "./copyright-year";
import { BrandMark } from "@/components/ui/brand-mark";
import { branding } from "@/lib/branding.config";
import { legal } from "@/lib/legal.config";
import { compliance, contactPhoneHref } from "@/lib/compliance.config";
import { GPU_TYPES, productsHref } from "@/lib/catalog/filters";
import { cn } from "@/lib/utils";

// Legal entity is per-project — edit `lib/legal.config.ts` to change.
const companyInfo = {
  legalName: legal("name"),
  registrationNumber: legal("registration"),
  address: legal("address"),
  email: compliance.contactEmail,
};

type FooterLink = { title: string; href: string };

// Footer sections data
// GPU_TYPES holds the rentable GPUs only (withdrawn ones are filtered out in
// lib/catalog/filters), so this column cannot list a card nobody can rent.
const gpuLinks: FooterLink[] = [
  ...GPU_TYPES.map(({ value, label }) => ({
    title: label,
    href: productsHref({ gpu: value }),
  })),
  { title: "All Cloud PCs", href: productsHref() },
];

// This column once linked `?useCase=gaming|rendering|…`, but `useCase` is not
// a param anything reads, so every link landed on the unfiltered grid. It
// lists the informational pages instead.
const productLinks: FooterLink[] = [
  { title: "Pricing", href: "/pricing" },
  { title: "How It Works", href: "/how-it-works" },
  { title: "Requirements", href: "/requirements" },
];

const supportLinks: FooterLink[] = [
  { title: "FAQ", href: "/faq" },
  { title: "Help & Contact", href: "/help" },
];

const accountLinks: FooterLink[] = [
  { title: "Dashboard", href: "/dashboard" },
  { title: "My Machines", href: "/machines" },
  { title: "Wallet", href: "/wallet" },
];

// Acquirers check for delivery, refund and dispute policies in the footer.
// They are sections of the Terms; the ids are the section ids in
// content/legal/terms-of-service.ts (the /refund-policy and /dispute-policy
// permalinks redirect to the same anchors).
const legalLinks: FooterLink[] = [
  { title: "Terms of Service", href: "/terms" },
  { title: "Privacy Policy", href: "/privacy" },
  { title: "Delivery Policy", href: "/terms#delivery-and-availability" },
  { title: "Refund Policy", href: "/terms#refunds" },
  { title: "Dispute Policy", href: "/terms#dispute-resolution" },
];

// The short policy row in the bottom bar.
const policyLinks: FooterLink[] = [
  { title: "Terms", href: "/terms" },
  { title: "Privacy", href: "/privacy" },
  { title: "Refunds", href: "/terms#refunds" },
];

type NavModuleData = { id: string; index: string; title: string; hue: string; links: FooterLink[] };

const LIME = "var(--primary)";
const LAVENDER = "var(--accent)";

const cloudModule: NavModuleData = { id: "cloud-pcs", index: "01", title: "Cloud PCs", hue: LIME, links: gpuLinks };
const productModule: NavModuleData = { id: "product", index: "02", title: "Product", hue: LAVENDER, links: productLinks };
const accountModule: NavModuleData = { id: "account", index: "03", title: "Account", hue: LIME, links: accountLinks };
const supportModule: NavModuleData = { id: "support", index: "04", title: "Support", hue: LIME, links: supportLinks };
const legalModule: NavModuleData = { id: "legal", index: "05", title: "Legal", hue: LAVENDER, links: legalLinks };

const LED_COUNT = 48;

function ledLevel(i: number, active: number): number | undefined {
  if (active < 0) return undefined;
  const d = Math.abs(i - active);
  if (d === 0) return 3;
  if (d === 1) return 2;
  if (d <= 3) return 1;
  return undefined;
}

// Small flat pixel glyphs for the background. p = lime, a = lavender, f = pale.
const GLYPHS = {
  spark: ["..p..", "..p..", "pp.pp", "..p..", "..p.."],
  gem: ["...a...", "..aaa..", ".afaaa.", "aaaaaaa", ".aaaaa.", "..aaa..", "...a..."],
  orb: ["..aaaa..", ".a....a.", "a..pp..a", "a.pppp.a", "a.pppp.a", "a..pp..a", ".a....a.", "..aaaa.."],
} as const;

const GLYPH_FILL: Record<string, string> = { p: "fill-primary", a: "fill-accent", f: "fill-foreground" };

function PixelGlyph({ name, className }: { name: keyof typeof GLYPHS; className?: string }) {
  const map = GLYPHS[name];
  return (
    <svg viewBox={`0 0 ${map[0].length} ${map.length}`} aria-hidden className={cn("pointer-events-none absolute h-auto", className)}>
      {map.map((row, y) =>
        row.split("").map((key, x) =>
          key === "." ? null : (
            <rect key={`${x}-${y}`} x={x + 0.08} y={y + 0.08} width={0.84} height={0.84} rx={0.16} className={GLYPH_FILL[key]} />
          ),
        ),
      )}
    </svg>
  );
}

function NavModule({
  module,
  open,
  onToggle,
  className,
}: {
  module: NavModuleData;
  open: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const listId = `footer-nav-${module.id}`;
  return (
    <div
      className={cn("fin-module relative", className)}
      data-open={open}
      style={{ "--hue": module.hue } as CSSProperties}
    >
      <span aria-hidden className="fin-bar absolute inset-x-6 top-0 hidden h-px md:block" />
      <h3 className="font-mono text-[11px] font-normal uppercase tracking-[0.2em]">
        {/* Mobile: an expandable row. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={listId}
          className="flex w-full items-center gap-2.5 py-3.5 text-left text-foreground/85 md:hidden"
        >
          <span aria-hidden className="fin-dot size-2 rounded-[2px]" />
          <span aria-hidden className="text-muted-foreground">{module.index}</span>
          <span className="flex-1">{module.title}</span>
          <span aria-hidden className="rounded-md bg-foreground/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {module.links.length}
          </span>
          <IconChevronDown aria-hidden className={cn("size-4 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
        {/* Tablet and up: a label over an always-open list. */}
        <span className="hidden items-center gap-2.5 text-foreground/85 md:flex">
          <span aria-hidden className="fin-dot size-2 rounded-[2px]" />
          <span aria-hidden className="text-muted-foreground">{module.index}</span>
          {module.title}
        </span>
      </h3>
      <ul
        id={listId}
        className={cn(
          "gap-y-2.5 pb-4 pl-3 md:mt-4 md:grid md:grid-cols-1 md:pb-0",
          module.links.length > 4 && "grid-cols-2 gap-x-4",
          open ? "grid" : "hidden",
        )}
      >
        {module.links.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className="fin-link text-sm">
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BackToTop() {
  const onClick = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="fin-top inline-flex h-11 items-center gap-2.5 rounded-xl pl-2 pr-4 font-mono text-[11px] uppercase tracking-[0.18em] text-primary"
    >
      <span aria-hidden className="relative grid size-7 place-items-center overflow-hidden rounded-lg bg-foreground/10">
        <IconArrowUp className="fin-top-arrow size-4" stroke={2.5} />
        <span className="fin-trail absolute bottom-0.5 left-1/2 size-1 -translate-x-1/2 rounded-[1px] bg-current" />
        <span
          className="fin-trail absolute bottom-0.5 left-1/2 size-[3px] -translate-x-[calc(50%+5px)] rounded-[1px] bg-current"
          style={{ "--delay": "120ms" } as CSSProperties}
        />
        <span
          className="fin-trail absolute bottom-0.5 left-1/2 size-[3px] translate-x-[2px] rounded-[1px] bg-current"
          style={{ "--delay": "240ms" } as CSSProperties}
        />
      </span>
      Back to top
    </button>
  );
}

export function Footer() {
  const rootRef = useRef<HTMLElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const frame = useRef(0);
  const [lit, setLit] = useState(false);
  const [led, setLed] = useState(-1);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // Mouse only: the wordmark light, the lit pixel grid and the indicator
  // strip follow the pointer. Touch screens keep the resting glow.
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== "mouse") return;
    const { clientX, clientY } = e;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      const box = root.getBoundingClientRect();
      root.style.setProperty("--mx", `${clientX - box.left}px`);
      root.style.setProperty("--my", `${clientY - box.top}px`);
      const strip = stripRef.current?.getBoundingClientRect();
      if (strip && strip.width > 0) {
        const index = Math.floor(((clientX - strip.left) / strip.width) * LED_COUNT);
        setLed(Math.min(LED_COUNT - 1, Math.max(0, index)));
      }
      setLit(true);
    });
  };

  const onPointerLeave = () => {
    cancelAnimationFrame(frame.current);
    setLit(false);
    setLed(-1);
  };

  const toggle = (id: string) => setOpenId((current) => (current === id ? null : id));
  const moduleProps = (module: NavModuleData) => ({
    module,
    open: openId === module.id,
    onToggle: () => toggle(module.id),
  });

  return (
    <footer
      ref={rootRef}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      data-lit={lit ? "on" : "off"}
      className="relative isolate overflow-hidden bg-background"
    >
      {/* Background: navy screen rising into lavender, light beam, pixel grid, wordmark, glyphs */}
      <div aria-hidden className="fin-environment pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="fin-beam pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="fin-pixels pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="fin-pixels-dots pointer-events-none absolute inset-0 -z-10" />

      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 select-none">
        <div className="absolute inset-x-0 bottom-0 flex translate-y-[26%] justify-center">
          <span className="fin-wordmark whitespace-nowrap font-heading font-bold uppercase">{branding.name}</span>
        </div>
        <div className="fin-wordmark-light absolute inset-0">
          <div className="absolute inset-x-0 bottom-0 flex translate-y-[26%] justify-center">
            <span className="fin-wordmark fin-wordmark-lit whitespace-nowrap font-heading font-bold uppercase">
              {branding.name}
            </span>
          </div>
        </div>
      </div>

      <PixelGlyph name="spark" className="twinkle right-[7%] top-10 -z-10 w-5 opacity-80" />
      <PixelGlyph name="spark" className="twinkle left-[46%] top-24 -z-10 hidden w-3 opacity-60 [animation-delay:-1.4s] lg:block" />
      <PixelGlyph name="gem" className="bob-b bottom-[34%] left-[2.5%] -z-10 hidden w-6 opacity-50 md:block" />
      <PixelGlyph name="orb" className="bob-a right-[2%] top-[42%] -z-10 hidden w-8 opacity-40 xl:block" />

      {/* Seam with the page above */}
      <div aria-hidden className="fin-seam pointer-events-none absolute inset-x-0 top-0 h-px" />
      <div aria-hidden className="fin-seam-pixels pointer-events-none absolute inset-x-0 top-[6px] h-[5px] opacity-70" />

      <div className="relative mx-auto w-full max-w-[1440px] px-4 pb-8 pt-16 sm:px-8 lg:px-12 lg:pt-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start xl:gap-14">
          {/* Brand and the final screen */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Link href="/" className="group inline-flex rounded-xl">
                <BrandMark />
              </Link>
              <span className="inline-flex items-center gap-2 rounded-full bg-foreground/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/70 ring-1 ring-foreground/10">
                <span aria-hidden className="fin-blink size-1.5 rounded-[1px] bg-primary shadow-[0_0_8px_var(--primary)]" />
                Final screen
              </span>
            </div>

            <h2 className="mt-7 font-heading text-[2.6rem] font-bold uppercase leading-[0.95] tracking-tight text-foreground sm:text-6xl lg:text-[4.25rem]">
              Game over?
              <br />
              <span className="fin-never">Never.</span>
            </h2>
            <p className="mt-5 font-heading text-lg font-medium text-foreground">Your next session starts here.</p>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground/65">
              Windows cloud PCs with NVIDIA GPUs for gaming, rendering, AI and compute. Hourly rates, metered per minute.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                href={productsHref()}
                className="fin-continue inline-flex h-11 items-center gap-2 rounded-xl px-4 font-heading text-sm font-semibold text-foreground"
              >
                <IconPlayerPlayFilled aria-hidden className="size-4 text-primary" />
                Continue: pick a GPU
              </Link>
              <BackToTop />
            </div>
          </div>

          {/* Navigation console */}
          <nav aria-label="Footer" className="fin-console rounded-2xl">
            <div className="flex items-center justify-between border-b border-foreground/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:px-6">
              <span className="flex items-center gap-2">
                <span aria-hidden className="flex gap-1">
                  <span className="size-1.5 rounded-[1px] bg-primary" />
                  <span className="size-1.5 rounded-[1px] bg-accent" />
                  <span className="size-1.5 rounded-[1px] bg-foreground/25" />
                </span>
                Menu
              </span>
              <span>
                <span className="md:hidden">Tap a section</span>
                <span className="hidden md:inline">Select destination</span>
              </span>
            </div>

            <div className="divide-y divide-foreground/10 px-4 sm:px-6 md:grid md:grid-cols-4 md:divide-x md:divide-y-0 md:px-0 md:py-5">
              <NavModule {...moduleProps(cloudModule)} className="md:px-6" />
              <div className="divide-y divide-foreground/10 md:space-y-7 md:divide-y-0 md:px-6">
                <NavModule {...moduleProps(productModule)} />
                <NavModule {...moduleProps(supportModule)} />
              </div>
              <NavModule {...moduleProps(accountModule)} className="md:px-6" />
              <NavModule {...moduleProps(legalModule)} className="md:px-6" />
            </div>
          </nav>
        </div>

        {/* Pixel indicator strip: lights up under the pointer */}
        <div aria-hidden className="mt-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span className="shrink-0 text-primary/80">P1 ready</span>
          <div ref={stripRef} className="flex h-3 flex-1 items-center gap-[3px] sm:gap-1">
            {Array.from({ length: LED_COUNT }, (_, i) => (
              <span
                key={i}
                className="fin-led h-1.5 flex-1 rounded-[1px]"
                data-level={ledLevel(i, led)}
                style={{ "--i": i } as CSSProperties}
              />
            ))}
          </div>
          <span className="hidden shrink-0 sm:inline">Continues: unlimited</span>
        </div>

        {/* Bottom bar */}
        <div className="fin-bottom mt-4 rounded-2xl px-4 py-4 sm:px-6" data-nosnippet>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              <span className="font-mono text-foreground/80">
                &copy; <CopyrightYear /> {branding.name}
              </span>
              <span aria-hidden="true">/</span>
              <span>{companyInfo.legalName}</span>
              <span aria-hidden="true">/</span>
              <span>Reg. no. {companyInfo.registrationNumber}</span>
              <span aria-hidden="true">/</span>
              <span>{companyInfo.address}</span>
            </div>

            {/* Payment Methods: the official white card marks, unaltered. */}
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">We accept</span>
              <div className="flex items-center gap-2">
                <Image src="/assets/icons/payment/visa.svg" alt="Visa" width={44} height={30} />
                <Image src="/assets/icons/payment/mastercard.svg" alt="Mastercard" width={44} height={30} />
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-3 border-t border-foreground/10 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div
              className="flex flex-wrap items-center gap-x-5 gap-y-1.5 pl-3 text-xs"
              style={{ "--hue": LIME } as CSSProperties}
            >
              <a href={`mailto:${companyInfo.email}`} className="fin-link gap-1.5 break-all">
                <IconMail aria-hidden className="size-3.5 shrink-0 text-primary" />
                {companyInfo.email}
              </a>
              {compliance.contactPhone && (
                <a href={contactPhoneHref!} className="fin-link gap-1.5">
                  <IconPhone aria-hidden className="size-3.5 shrink-0 text-primary" />
                  {compliance.contactPhone}
                </a>
              )}
            </div>
            <nav
              aria-label="Policies"
              className="flex flex-wrap items-center gap-x-5 gap-y-1.5 pl-3 text-xs"
              style={{ "--hue": LAVENDER } as CSSProperties}
            >
              {policyLinks.map((link) => (
                <Link key={link.href} href={link.href} className="fin-link">
                  {link.title}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
